import React, { useState, useEffect, useContext, useMemo } from 'react';
import api, { BACKEND_URL } from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { 
  BookOpen, 
  Plus, 
  Trash2, 
  Smartphone, 
  Search,
  Calendar as CalendarIcon,
  X,
  ExternalLink,
  Mail,
  Pencil,
  LayoutGrid,
  ChevronLeft,
  ChevronRight,
  Info
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import DocumentViewer from '../components/DocumentViewer';

const IELTSBooking = () => {
  const { user } = useContext(AuthContext);
  const [bookings, setBookings] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  
  const [editingBooking, setEditingBooking] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    student_name: '',
    email: '',
    mobile_no: '',
    passport_no: '',
    test_date: '',
    payment_status: 'Not Done',
    passport: null
  });

  const [students, setStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Calendar States
  const [activeMonth, setActiveMonth] = useState(new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' }));
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(new Date().toISOString().split('T')[0]);

  // Document Viewer State
  const [viewerDoc, setViewerDoc] = useState(null);
  const openViewer = (url, title) => setViewerDoc({ url, title });

  useEffect(() => {
    fetchBookings();
    api.get('/students').then(res => setStudents(res.data)).catch(() => {});
  }, []);

  const fetchBookings = async () => {
    try {
      const res = await api.get('/ielts');
      setBookings(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setLoading(false);
    }
  };

  // Calendar Logic
  const bookingMap = useMemo(() => {
    const map = {};
    bookings.forEach(b => {
      if (!b.test_date) return;
      const dateKey = new Date(b.test_date).toISOString().split('T')[0];
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(b);
    });
    return map;
  }, [bookings]);

  const calendarGrid = useMemo(() => {
    const [monthName, yearStr] = activeMonth.split(' ');
    const monthIdx = new Date(Date.parse(monthName + " 1, 2012")).getMonth();
    const year = parseInt(yearStr);
    const firstDay = new Date(year, monthIdx, 1).getDay();
    const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
    const grid = [];
    let day = 1;
    for (let i = 0; i < 6; i++) {
      const week = [];
      for (let j = 0; j < 7; j++) {
        if ((i === 0 && j < firstDay) || day > daysInMonth) week.push(null);
        else {
          const dateStr = `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          week.push(dateStr);
          day++;
        }
      }
      grid.push(week);
      if (day > daysInMonth) break;
    }
    return grid;
  }, [activeMonth]);

  const changeMonth = (offset) => {
    const [m, y] = activeMonth.split(' ');
    const d = new Date(Date.parse(m + " 1, " + y));
    d.setMonth(d.getMonth() + offset);
    setActiveMonth(d.toLocaleString('en-US', { month: 'long', year: 'numeric' }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 5 * 1024 * 1024) {
      setFormErrors(prev => ({ ...prev, passport: `File too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum allowed size is 5 MB.` }));
      e.target.value = '';
      setFormData({ ...formData, passport: null });
      return;
    }
    setFormErrors(prev => ({ ...prev, passport: undefined }));
    setFormData({ ...formData, passport: file });
  };



  const validateForm = () => {
    const newErrors = {};
    if (!formData.student_name.trim()) newErrors.student_name = 'Required';
    if (!formData.test_date) newErrors.test_date = 'Required';
    if (!editingBooking && !formData.passport) newErrors.passport = 'Passport scan is required';
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const data = new FormData();
    Object.keys(formData).forEach(key => {
      if (key !== 'passport') data.append(key, formData[key] ?? '');
    });
    if (formData.passport) data.append('passport', formData.passport);
    else if (editingBooking) data.append('passport_path', editingBooking.passport_path);

    try {
      if (editingBooking) await api.put(`/ielts/${editingBooking.id}`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
      else await api.post('/ielts', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      handleCloseModal();
      fetchBookings();
    } catch (err) {
      const msg = err.response?.data?.error || 'Error saving booking';
      setFormErrors({ submit: msg });
    }
  };

  const handleEdit = (booking) => {
    setEditingBooking(booking);
    setFormData({
      student_name: booking.student_name,
      email: booking.email || '',
      mobile_no: booking.mobile_no || '',
      passport_no: booking.passport_no || '',
      test_date: booking.test_date ? booking.test_date.split('T')[0] : '',
      payment_status: booking.payment_status,
      passport: null
    });
    setSelectedStudent(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingBooking(null);
    setFormErrors({});
    setFormData({ student_name: '', email: '', mobile_no: '', passport_no: '', test_date: '', payment_status: 'Not Done', passport: null });
    setStudentSearch('');
    setSelectedStudent(null);
    setShowStudentDropdown(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this booking?')) {
      try {
        await api.delete(`/ielts/${id}`);
        fetchBookings();
      } catch (err) {
        alert('Error deleting');
      }
    }
  };

  const filteredStudents = students.filter(s => s.full_name.toLowerCase().includes(studentSearch.toLowerCase())).slice(0, 8);

  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    setFormData(prev => ({
      ...prev,
      student_name: student.full_name,
      email: student.email || '',
      mobile_no: student.phone || '',
      passport_no: student.passport_no || ''
    }));
    setStudentSearch(student.full_name);
    setShowStudentDropdown(false);
  };

  const filteredBookings = bookings.filter(b => b.student_name.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-8 animate-in fade-in duration-700">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div>
          <h1 className="text-4xl font-black text-gray-900 tracking-tighter flex items-center gap-4">
             <div className="p-3 bg-indigo-600 text-white rounded-2xl shadow-xl shadow-indigo-100">
                <BookOpen size={28} />
             </div>
             IELTS List
          </h1>
          <p className="text-sm font-black text-indigo-400 uppercase tracking-[0.3em] mt-3">IELTS Test Booking System</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="bg-white p-1 rounded-2xl border border-gray-100 shadow-sm flex items-center">
             <button onClick={() => setViewMode('list')} className={`p-2.5 rounded-xl transition-all ${viewMode === 'list' ? 'bg-indigo-600 text-white shadow-lg' : 'text-gray-400 hover:bg-gray-50'}`}><LayoutGrid size={20} /></button>
             <button onClick={() => setViewMode('calendar')} className={`p-2.5 rounded-xl transition-all ${viewMode === 'calendar' ? 'bg-indigo-600 text-white shadow-lg' : 'text-gray-400 hover:bg-gray-50'}`}><CalendarIcon size={20} /></button>
          </div>
          <button 
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-8 py-3 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest transition-all shadow-xl shadow-indigo-100 hover:bg-indigo-700 active:scale-95"
          >
            <Plus size={20} /> Add New Booking
          </button>
        </div>
      </div>

      {viewMode === 'list' ? (
        <div className="space-y-6">
           <div className="relative max-w-2xl">
              <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input type="text" placeholder="Search registry by student name..." className="w-full pl-16 pr-8 py-4 bg-white border border-gray-100 rounded-[2rem] shadow-xl shadow-indigo-900/5 focus:ring-2 focus:ring-indigo-600 outline-none font-medium" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
           </div>

           <div className="bg-white rounded-[3rem] border border-gray-100 shadow-xl shadow-indigo-900/5 overflow-hidden border-b-8 border-b-indigo-600">
              <div className="overflow-x-auto">
                 <table className="w-full text-left">
                    <thead>
                       <tr className="bg-gray-50/50 text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                          <th className="px-8 py-6">Student Name</th>
                          <th className="px-8 py-6">Contact Info</th>
                          <th className="px-8 py-6">Test Date</th>
                          <th className="px-8 py-6">Payment</th>
                          <th className="px-8 py-6 text-right">Actions</th>
                       </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                       {loading ? (
                          <tr><td colSpan="5" className="p-20 text-center"><div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mx-auto"></div></td></tr>
                       ) : filteredBookings.map(b => (
                          <tr key={b.id} className="hover:bg-indigo-50/20 transition-all group">
                             <td className="px-8 py-6">
                               <span className="text-sm font-black text-gray-900 group-hover:text-indigo-600 transition-colors">{b.student_name}</span>
                             </td>
                             <td className="px-8 py-6 space-y-1">
                                <div className="flex items-center gap-2 text-xs font-bold text-gray-600"><Mail size={12} className="text-gray-400" />{b.email || 'N/A'}</div>
                                <div className="flex items-center gap-2 text-xs font-bold text-gray-400"><Smartphone size={12} />{b.mobile_no}</div>
                             </td>
                             <td className="px-8 py-6"><div className="flex items-center gap-2 text-sm font-black text-gray-900"><CalendarIcon size={14} className="text-indigo-400" />{b.test_date ? new Date(b.test_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Pending'}</div></td>
                             <td className="px-8 py-6"><span className={`px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest ${b.payment_status === 'Done' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-rose-50 text-rose-600 border border-rose-100'}`}>{b.payment_status}</span></td>
                             <td className="px-8 py-6">
                                <div className="flex items-center justify-end gap-1 transition-opacity">
                                   {b.passport_path && (
                                     <button 
                                       type="button"
                                       onClick={() => openViewer(b.passport_path.startsWith('http') ? b.passport_path : `${BACKEND_URL}${b.passport_path}`, `${b.student_name}'s Passport`)}
                                       className="p-2.5 bg-gray-50 text-gray-600 rounded-xl hover:bg-indigo-600 hover:text-white transition-all"
                                       title="View Passport"
                                     >
                                       <ExternalLink size={16} />
                                     </button>
                                   )}
                                   <button onClick={() => handleEdit(b)} className="p-2.5 bg-gray-50 text-amber-600 rounded-xl hover:bg-amber-600 hover:text-white transition-all"><Pencil size={16} /></button>
                                   {user?.role === 'Admin' && <button onClick={() => handleDelete(b.id)} className="p-2.5 bg-gray-50 text-rose-600 rounded-xl hover:bg-rose-600 hover:text-white transition-all"><Trash2 size={16} /></button>}
                                </div>
                             </td>
                          </tr>
                       ))}
                    </tbody>
                 </table>
              </div>
           </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start animate-in zoom-in-95 duration-500">
           <div className="lg:col-span-8 bg-white p-8 rounded-[3rem] border border-gray-100 shadow-xl shadow-indigo-900/5">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <button onClick={() => changeMonth(-1)} className="p-3 hover:bg-indigo-50 rounded-xl transition-colors"><ChevronLeft size={20} /></button>
                  <h3 className="text-2xl font-black text-gray-900 tracking-tight min-w-[200px] text-center">{activeMonth}</h3>
                  <button onClick={() => changeMonth(1)} className="p-3 hover:bg-indigo-50 rounded-xl transition-colors"><ChevronRight size={20} /></button>
                </div>
              </div>
              <div className="grid grid-cols-7 gap-3">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d} className="text-center text-[10px] font-black text-gray-300 uppercase tracking-widest pb-4">{d}</div>)}
                {calendarGrid.flat().map((d, i) => {
                  const dayBookings = d && bookingMap[d];
                  const isSelected = selectedCalendarDate === d;
                  return (
                    <div key={i} className="aspect-square relative">
                      {d && (
                        <button onClick={() => setSelectedCalendarDate(d)} className={`w-full h-full rounded-2xl flex flex-col items-center justify-center transition-all duration-300 border-2 ${isSelected ? 'border-indigo-600 shadow-xl scale-105 z-10' : 'border-transparent'} ${dayBookings ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}`}>
                          <span className="text-sm font-black">{parseInt(d.split('-')[2])}</span>
                          {dayBookings && <div className="absolute bottom-2 w-1.5 h-1.5 rounded-full bg-white/60"></div>}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
           </div>

           <div className="lg:col-span-4 sticky top-8 space-y-6">
              <div className="bg-indigo-900 p-8 rounded-[2.5rem] text-white shadow-2xl shadow-indigo-900/20 min-h-[400px]">
                <div className="space-y-1 mb-8">
                  <p className="text-[9px] font-black uppercase tracking-[0.3em] opacity-40">Booking Details</p>
                  <h3 className="text-xl font-black">{new Date(selectedCalendarDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</h3>
                  <div className="w-10 h-0.5 bg-indigo-400/30 mt-3"></div>
                </div>
                <AnimatePresence mode="wait">
                  <motion.div key={selectedCalendarDate} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                    {!bookingMap[selectedCalendarDate] ? (
                      <div className="py-20 text-center opacity-30 flex flex-col items-center">
                        <Info size={40} className="mb-4" />
                        <p className="text-[10px] font-black uppercase tracking-widest">No Bookings Found</p>
                      </div>
                    ) : (
                      bookingMap[selectedCalendarDate].map((b, idx) => (
                        <div key={idx} className="bg-white/5 p-6 rounded-3xl border border-white/10 backdrop-blur-sm space-y-3">
                          <h4 className="font-black text-sm">{b.student_name}</h4>
                          <div className="pt-3 border-t border-white/10 flex justify-between items-center text-[9px] font-black uppercase tracking-widest">
                             <span className={b.payment_status === 'Done' ? 'text-emerald-400' : 'text-rose-400'}>{b.payment_status}</span>
                             <span className="opacity-40">{b.mobile_no}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
           </div>
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={handleCloseModal} className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" />
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="relative w-full max-w-lg bg-white rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
               <div className="p-8 border-b border-gray-50 flex justify-between items-center shrink-0">
                  <h2 className="text-xl font-black text-gray-900 uppercase tracking-tight">{editingBooking ? 'Edit Booking' : 'Add New Booking'}</h2>
                  <button onClick={handleCloseModal} className="p-2 hover:bg-gray-50 rounded-xl transition-all"><X size={20} className="text-gray-400" /></button>
               </div>
               <form onSubmit={handleSubmit} className="p-8 space-y-5 overflow-y-auto flex-1">
                  <div className="space-y-4">
                     <div className="grid grid-cols-1 gap-4">

                        {/* Student Search / Select */}
                        {!editingBooking ? (
                          <div className="space-y-1.5 relative">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Select Student</label>
                            <input
                              type="text"
                              placeholder="Search student..."
                              className={`w-full px-5 py-3 bg-gray-50 rounded-xl border-none outline-none font-bold text-gray-900 focus:ring-2 focus:ring-indigo-600 transition-all ${formErrors.student_name ? 'ring-2 ring-rose-400' : ''}`}
                              value={studentSearch}
                              onChange={e => { setStudentSearch(e.target.value); setShowStudentDropdown(true); setSelectedStudent(null); }}
                              onFocus={() => setShowStudentDropdown(true)}
                            />
                            {formErrors.student_name && <p className="text-xs font-bold text-rose-500 ml-1">Please select a student</p>}
                            {showStudentDropdown && studentSearch && filteredStudents.length > 0 && (
                              <div className="absolute z-50 w-full bg-white border border-gray-100 rounded-2xl shadow-xl mt-1 overflow-hidden">
                                {filteredStudents.map(s => (
                                  <button
                                    key={s.id}
                                    type="button"
                                    className="w-full text-left px-5 py-3 hover:bg-indigo-50 transition-colors"
                                    onMouseDown={() => handleSelectStudent(s)}
                                  >
                                    <p className="text-sm font-black text-gray-900">{s.full_name}</p>
                                    <p className="text-xs font-bold text-gray-400">{s.passport_no || 'No passport'} · {s.phone || 'No phone'}</p>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : null}

                        {/* Auto-filled read-only fields */}
                        {(selectedStudent || editingBooking) && (
                          <div className="bg-indigo-50/60 rounded-2xl p-4 space-y-3">
                            <p className="text-[9px] font-black text-indigo-400 uppercase tracking-widest">Student Info (Auto-filled)</p>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Name</p>
                                <p className="text-sm font-black text-gray-900">{formData.student_name || '—'}</p>
                              </div>
                              <div>
                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Passport No</p>
                                <p className="text-sm font-black text-gray-900">{formData.passport_no || '—'}</p>
                              </div>
                              <div>
                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Email</p>
                                <p className="text-sm font-bold text-gray-600">{formData.email || '—'}</p>
                              </div>
                              <div>
                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Mobile</p>
                                <p className="text-sm font-bold text-gray-600">{formData.mobile_no || '—'}</p>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                           <div className="space-y-1.5">
                              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Test Date</label>
                              <input type="date" name="test_date" className={`w-full px-5 py-3 bg-gray-50 rounded-xl border-none outline-none font-bold text-gray-900 ${formErrors.test_date ? 'ring-2 ring-rose-400' : ''}`} value={formData.test_date} onChange={handleInputChange} required />
                              {formErrors.test_date && <p className="text-xs font-bold text-rose-500 ml-1">Test date is required</p>}
                           </div>
                           <div className="space-y-1.5">
                              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Payment</label>
                              <select name="payment_status" className="w-full px-5 py-3 bg-gray-50 rounded-xl border-none outline-none font-bold text-gray-900" value={formData.payment_status} onChange={handleInputChange}>
                                 <option value="Not Done">Not Done</option>
                                 <option value="Done">Done</option>
                              </select>
                           </div>
                        </div>
                        <div className="space-y-1.5">
                           <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Passport Scan</label>
                           <input type="file" onChange={handleFileChange} accept="image/jpeg,image/jpg,image/png,application/pdf" className={`w-full text-xs text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-black file:bg-indigo-50 file:text-indigo-600 hover:file:bg-indigo-100 ${formErrors.passport ? 'ring-2 ring-rose-400 rounded-xl' : ''}`} />
                           <p className="text-[10px] font-bold text-gray-400 ml-1">JPG, PNG or PDF · Max 5 MB</p>
                           {formErrors.passport && <p className="text-xs font-bold text-rose-500 ml-1">{formErrors.passport}</p>}
                           {editingBooking && editingBooking.passport_path && (
                              <div className="mt-2 flex items-center gap-2">
                                 <span className="text-[10px] text-gray-500 font-bold">Current Passport:</span>
                                 <button
                                    type="button"
                                    onClick={() => openViewer(editingBooking.passport_path.startsWith('http') ? editingBooking.passport_path : `${BACKEND_URL}${editingBooking.passport_path}`, `${editingBooking.student_name}'s Passport`)}
                                    className="text-xs text-indigo-600 hover:underline font-black"
                                 >
                                    View Passport
                                 </button>
                              </div>
                           )}
                        </div>
                     </div>
                  </div>
                  {formErrors.submit && <p className="text-xs font-bold text-rose-500 text-center">{formErrors.submit}</p>}
                  <div className="flex gap-3 pt-4">
                     <button type="button" onClick={handleCloseModal} className="flex-1 py-3.5 bg-gray-50 text-gray-500 font-black uppercase tracking-widest rounded-xl hover:bg-gray-100 transition-all">Cancel</button>
                     <button type="submit" className="flex-1 py-3.5 bg-indigo-600 text-white font-black uppercase tracking-widest rounded-xl shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all">Save Booking</button>
                  </div>
               </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      {viewerDoc && <DocumentViewer url={viewerDoc.url} title={viewerDoc.title} onClose={() => setViewerDoc(null)} />}
    </div>
  );
};

export default IELTSBooking;
